import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { StandardReportsTab } from './StandardReportsTab';
import { CustomReportsTab } from './CustomReportsTab';
import { InspectionReportsTab } from './InspectionReportsTab';
import { DailyServiceLogsTab } from './DailyServiceLogsTab';
import { ShiftReportsTab } from './ShiftReportsTab';
import type { UnitReportScope } from './ReportFilterBar';
import {
    buildInspectHubPath,
    buildUnitReportsPath,
    seedUnitReportFilters,
    type UnitReportsTabId,
} from '../../utils/unitReportsUtils';
import '../../css/ReportsLandingPage.css';
import { BackButton, Card, PageHeader, TabNav, getTabPanelProps } from '../ui';

const REPORTS_TAB_NAV_ID = 'reports';

const TABS = [
    { id: 'standard', label: 'Standard Reports' },
    { id: 'custom', label: 'Custom Reports' },
    { id: 'inspection', label: 'Inspection Reports' },
    { id: 'daily-service', label: 'Daily Service Logs' },
    { id: 'shift', label: 'Shift Reports' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const STORAGE_KEY = 'reportsActiveTab';
const DEFAULT_TAB: TabId = 'standard';

const VALID_TAB_IDS = new Set<string>(TABS.map((t) => t.id));

function getPersistedTab(): TabId {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && VALID_TAB_IDS.has(stored)) return stored as TabId;
    } catch {
        /* ignore */
    }
    return DEFAULT_TAB;
}

function resolveInitialTab(unitId: string | undefined, tabParam: string | null): TabId {
    if (tabParam && VALID_TAB_IDS.has(tabParam)) {
        return tabParam as TabId;
    }
    if (unitId) {
        return 'inspection';
    }
    return getPersistedTab();
}

/**
 * Reports Landing Page — replaces the old ReportsContent.
 * TabNav with Standard, Custom, Inspection, Daily Service Logs, and Shift Reports.
 * Persists active tab in localStorage.
 */
export const ReportsLandingPage = () => {
    const { unitId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const tabParam = searchParams.get('tab');
    const categoryIdParam = searchParams.get('categoryId');
    const { companyUser, companyOwner, company } = useUserContext();
    const { companyUnits } = useEquipmentContext();
    const [activeTab, setActiveTab] = useState<TabId>(() => resolveInitialTab(unitId, tabParam));

    const currentUser = companyOwner || companyUser;
    const companyId = currentUser?.companyId || company?.id || '';

    const scopedUnit = useMemo(
        () => (unitId ? companyUnits.find((unit) => unit.id === unitId) ?? null : null),
        [companyUnits, unitId],
    );

    const unitScope = useMemo<UnitReportScope | undefined>(() => {
        if (!unitId) {
            return undefined;
        }
        const equipmentCategoryId = scopedUnit?.categoryId || categoryIdParam || '';
        if (!equipmentCategoryId) {
            return undefined;
        }
        return {
            unitId,
            equipmentCategoryId,
        };
    }, [categoryIdParam, scopedUnit?.categoryId, unitId]);

    const userId = currentUser?.id || '';

    useEffect(() => {
        setActiveTab(resolveInitialTab(unitId, tabParam));
    }, [unitId, tabParam]);

    useEffect(() => {
        if (!unitScope) {
            return;
        }
        seedUnitReportFilters(unitScope.equipmentCategoryId, unitScope.unitId);
    }, [unitScope]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, activeTab);
        } catch {
            /* ignore */
        }
    }, [activeTab]);

    const backTo =
        unitId && (unitScope?.equipmentCategoryId || categoryIdParam)
            ? buildInspectHubPath(unitScope?.equipmentCategoryId || categoryIdParam || '', unitId)
            : '/';

    const pageHeaderProps = scopedUnit
        ? {
              title: 'Reports',
              subtitle: scopedUnit.description ? (
                  <>
                      <strong>{scopedUnit.name}</strong>
                      {` - ${scopedUnit.description}`}
                  </>
              ) : (
                  <strong>{scopedUnit.name}</strong>
              ),
          }
        : { title: 'Reports' };

    const handleTabChange = (tabId: string) => {
        if (!VALID_TAB_IDS.has(tabId)) {
            return;
        }
        const nextTab = tabId as TabId;
        setActiveTab(nextTab);
        if (unitId) {
            navigate(
                buildUnitReportsPath(unitId, {
                    tab: nextTab as UnitReportsTabId,
                    equipmentCategoryId: unitScope?.equipmentCategoryId,
                }),
                { replace: true },
            );
        }
    };

    const panelProps = getTabPanelProps(REPORTS_TAB_NAV_ID, activeTab);

    return (
        <div className="page-container reports-landing-page">
            <PageHeader {...pageHeaderProps} />
            <BackButton fallbackTo={backTo} />

            <div className="tab-component">
                <TabNav
                    id={REPORTS_TAB_NAV_ID}
                    variant="tabs"
                    ariaLabel="Report tabs"
                    flared
                    tabs={[...TABS]}
                    activeTabId={activeTab}
                    onTabChange={handleTabChange}
                />

                <Card className="tab-body reports-tab-body" variant="card-lg" {...panelProps}>
                    {activeTab === 'standard' && <StandardReportsTab companyId={companyId} userId={userId} />}
                    {activeTab === 'custom' && <CustomReportsTab companyId={companyId} />}
                    {activeTab === 'inspection' && (
                        <InspectionReportsTab companyId={companyId} unitScope={unitScope} />
                    )}
                    {activeTab === 'daily-service' && (
                        <DailyServiceLogsTab companyId={companyId} unitScope={unitScope} />
                    )}
                    {activeTab === 'shift' && <ShiftReportsTab companyId={companyId} unitScope={unitScope} />}
                </Card>
            </div>
        </div>
    );
};
